import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui';
import { Badge } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

interface RiskProfileItem {
  id: string;
  academyId: string;
  academyName: string | null;
  riskLevel: string;
  currentBalance: number;
  disputeRate: number;
  totalDisputes: number;
}

export default function RiskPage() {
  const [profiles, setProfiles] = useState<RiskProfileItem[]>([]);
  const [pagination, setPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    adminBackofficeApi
      .getRiskProfiles({ page, limit: 20 })
      .then((data) => {
        if (data.success && data.data) {
          setProfiles(data.data.profiles ?? []);
          setPagination(data.data.pagination ?? null);
        } else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Risk profiles"
        help={<p>Academy risk overview — balance, dispute rates, and risk levels.</p>}
      />
      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">Academy risk overview</h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 font-medium">Academy</th>
                  <th className="text-left py-2 font-medium">Risk level</th>
                  <th className="text-right py-2 font-medium">Balance</th>
                  <th className="text-right py-2 font-medium">Dispute rate</th>
                  <th className="text-right py-2 font-medium">Disputes</th>
                  <th className="text-left py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2">
                      <span className="font-medium">{p.academyName ?? p.academyId}</span>
                    </td>
                    <td className="py-2">
                      <Badge
                        variant={p.riskLevel === 'high' || p.riskLevel === 'restricted' ? 'destructive' : 'secondary'}
                      >
                        {p.riskLevel}
                      </Badge>
                    </td>
                    <td className="py-2 text-right">
                      {(p.currentBalance / 100).toFixed(2)}
                    </td>
                    <td className="py-2 text-right">
                      {(p.disputeRate * 100).toFixed(2)}%
                    </td>
                    <td className="py-2 text-right">{p.totalDisputes}</td>
                    <td className="py-2">
                      <Link
                        to={`/backoffice/risk/${p.academyId}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {profiles.length === 0 && (
              <p className="py-8 text-center text-gray-500">No risk profiles found.</p>
            )}
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
